import { debounce, uniq } from 'lodash-es';
import type { TreeNode } from 'tree-model';

import { httpPostSearch } from '@iconik-io/iconik-api/src/services/search';
import { prepareQueryForMainSearch } from '@iconik-io/shared-lib/src/utils/search/prepare_query_for_main_search';

import {
  getCollectionContents,
  getCollections,
} from '../../../ducks/collection_fetch/thunks';
import { cancellable } from '../../../ducks/http/actions';
import {
  addBranches,
  clearTree,
  modifyBranchByID,
  modifyBranchByNode,
} from '../../../ducks/tree/thunks';
import type {
  TreeReducerId,
  TreeReducerModel,
} from '../../../ducks/tree/types';
import type { AppDispatch } from '../../../store/types';
import { UUID_ZERO } from '../../../utils/tree_handlers';

type ModalIds = TreeReducerId;

const getAncestorCollectionsTitles =
  (
    collections: {
      ancestor_collections?: string[] | null;
      id: string;
      title: string;
    }[],
  ) =>
  (dispatch: AppDispatch) => {
    const ancestorCollections = collections.flatMap((collection) => {
      if (collection.ancestor_collections) {
        return collection.ancestor_collections;
      }
      return [];
    });
    dispatch(
      cancellable(
        httpPostSearch(
          {
            query: '',
            include_fields: ['id', 'title'],
            doc_types: ['collections'],
            filter: {
              filters: [
                {
                  operator: 'AND',
                  terms: [
                    {
                      name: 'status',
                      value: 'ACTIVE',
                    },
                    {
                      name: 'id',
                      value_in: uniq(ancestorCollections),
                    },
                  ],
                },
              ],
              operator: 'AND',
            },
            sort: [{ name: 'title', order: 'asc' }],
          },
          { save_search_history: false, per_page: 1000 },
        ),
      ),
    );
  };

const search =
  (query: string, modalId: ModalIds, perPage = 100, page = 1) =>
  async (dispatch: AppDispatch) => {
    if (!query) {
      dispatch(clearTree(modalId));
      await dispatch(getCollections({}, modalId));
    } else {
      const response = await dispatch(
        cancellable(
          httpPostSearch(
            {
              query: prepareQueryForMainSearch(query),
              doc_types: ['collections'],
              search_fields: ['title'],
              filter: {
                filters: [
                  {
                    operator: 'AND',
                    terms: [{ name: 'status', value: 'ACTIVE' }],
                  },
                ],
                operator: 'AND',
              },
              sort: [{ name: 'title', order: 'asc' }],
            },
            { save_search_history: false, per_page: perPage, page },
          ),
        ),
      );
      dispatch(
        modifyBranchByID(modalId, UUID_ZERO, {
          loading: false,
        }),
      );

      const { objects, page: collectionsPage, pages, total } = response.body;
      if (!collectionsPage || Number(collectionsPage) === 1) {
        dispatch(clearTree(modalId));
      }
      dispatch(getAncestorCollectionsTitles(objects));
      dispatch(
        addBranches(modalId, {
          objects,
          data: { total, page: collectionsPage, pages },
        }),
      );
    }
  };

export const searchCollectionTree = (
  query = '',
  modalId: ModalIds,
  page?: number,
) => search(query, modalId, 1000, page);

export const searchCollectionTreeDebounced = (
  query = '',
  modalId: ModalIds,
  page?: number,
) => debounce(search(query, modalId, 1000, page), 400);

export const handleOnExpand =
  (node: TreeNode<TreeReducerModel>, modalId: ModalIds) =>
  (dispatch: AppDispatch) => {
    if (!node.model.expanded && !node.children.length) {
      dispatch(getCollectionContents(node.model.id, {}, modalId));
    }
    dispatch(
      modifyBranchByNode(modalId, node, {
        expanded: !node.model.expanded,
      }),
    );

    // if the root element gets collapsed we refresh the tree
    if (node.model.id === UUID_ZERO && node.model.expanded) {
      dispatch(clearTree(modalId));
      dispatch(getCollections({}, modalId));
    }
  };
